import React from 'react'
import { useTabAuth } from '@/hooks/useTabAuth'

// 导入图标
import icMenuChats from '../../../assets/images/ic_menu_chats.png'
import icMenuChatsEd from '../../../assets/images/ic_menu_chats_ed.png'
import icMenuContact from '../../../assets/images/ic_menu_contact.png'
import icMenuContactEd from '../../../assets/images/ic_menu_contact_ed.png'
import icMenuSettings from '../../../assets/images/ic_menu_settings.png'
import icMenuSettingsEd from '../../../assets/images/ic_menu_settings_ed.png'
import { useChatStore } from '@/stores/useChatStore'
import NotificationIndicator from '@/components/NotificationIndicator'
import { logPrint } from '@/utils/my-utils'
import { MainPageType } from '@/stores/usePageDataStore'


interface MainSidebarProps {
  mainPageMenu: MainPageType
  onModuleChange: (module: MainPageType) => void
  onResetConnection: () => Promise<void>
}

// 连接状态指示器组件
const ConnectionIndicator = React.memo(({ onResetConnection }: { onResetConnection: () => Promise<void> }) => {
  const isConnected = useChatStore((state: any) => state.isConnected)
  const [isRefreshing, setIsRefreshing] = React.useState(false)

  const handleRefresh = React.useCallback(async () => {
    if (isRefreshing) return

    setIsRefreshing(true)

    try {
      await onResetConnection()
    } catch (error) {
      console.error('重置连接失败:', error)
    } finally {
      // 5秒后才允许再次点击
      setTimeout(() => {
        setIsRefreshing(false)
      }, 5000)
    }
  }, [onResetConnection, isRefreshing])

  return (
    <div className="flex items-center justify-center -mb-3">
      <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
      <span className="ml-2 text-xs text-gray-600">
        {isConnected ? '已连接' : '未连接'}
      </span>
      {!isConnected && (
        <button
          className={`ml-2 p-1 rounded-full transition-colors ${isRefreshing
            ? 'cursor-not-allowed opacity-50'
            : 'hover:bg-gray-200'
            }`}
          title={isRefreshing ? '刷新中...' : '刷新连接'}
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <svg
            className={`w-3 h-3 text-gray-600 ${isRefreshing ? 'animate-spin' : ''
              }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      )}
    </div>
  )
})

ConnectionIndicator.displayName = 'ConnectionIndicator'

// 用户头像组件
const UserAvatar = React.memo(() => {
  const tabAuth = useTabAuth()
  const userInfo = tabAuth.getUserInfo()

  return (
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-200 mb-1">
        <img
          src={userInfo?.avatar}
          alt={userInfo?.nickname?.slice(0, 2)}
          className="w-full h-full object-cover"
          onClick={() => {
            logPrint("当前登录用户：", tabAuth.getUserInfo())
          }}
        />
      </div>
      <div className="max-w-24 truncate text-xs text-gray-600">{userInfo?.nickname}</div>
    </div>
  )
})

UserAvatar.displayName = 'UserAvatar'

// 模块按钮组件
interface ModuleButtonProps {
  moduleKey: MainPageType
  label: string
  normalIcon: string
  activeIcon: string
  isActive: boolean
  onClick: (module: MainPageType) => void
}

const ModuleButton = React.memo(({
  moduleKey,
  label,
  normalIcon,
  activeIcon,
  isActive,
  onClick
}: ModuleButtonProps) => {
  return (
    <button
      onClick={() => onClick(moduleKey)}
      className={`
        relative flex flex-col items-center space-y-2 px-6 py-2 rounded-lg transition-all duration-200
        ${isActive ? 'bg-white shadow-sm' : 'hover:bg-white/10'}
      `}
      title={label}
    >
      <div className="w-6 h-6 flex items-center justify-center">
        <img
          src={isActive ? activeIcon : normalIcon}
          alt={label}
          className="w-6 h-6"
        />
        {/* 为聊天模块添加通知指示器 */}
        {moduleKey === 'chat' && (
          <NotificationIndicator className="absolute -top-1 -right-1" />
        )}
      </div>
      <span className={`text-xs font-medium ${isActive ? 'text-blue-600' : 'text-gray-600'}`}>
        {label}
      </span>
    </button>
  )
})

ModuleButton.displayName = 'ModuleButton'

export default function MainSidebar({
  mainPageMenu,
  onModuleChange,
  onResetConnection
}: MainSidebarProps): React.JSX.Element {
  return (
    <div className="w-32 bg-gray-100 flex flex-col items-center py-6 space-y-8 border-r border-gray-200">
      {/* 连接状态指示器 */}
      <ConnectionIndicator onResetConnection={onResetConnection} />

      {/* 用户头像 */}
      <UserAvatar />

      {/* 主要功能按钮 */}
      <div className="flex flex-col space-y-6">
        <ModuleButton
          moduleKey={MainPageType.CHAT}
          label="消息"
          normalIcon={icMenuChats}
          activeIcon={icMenuChatsEd}
          isActive={mainPageMenu === MainPageType.CHAT}
          onClick={onModuleChange}
        />
        <ModuleButton
          moduleKey={MainPageType.CONTACTS}
          label="通讯录"
          normalIcon={icMenuContact}
          activeIcon={icMenuContactEd}
          isActive={mainPageMenu === MainPageType.CONTACTS}
          onClick={onModuleChange}
        />
      </div>

      {/* 占位空白区域 */}
      <div className="flex-1"></div>

      {/* 系统功能按钮 */}
      <div className="flex flex-col space-y-6">
        <ModuleButton
          moduleKey={MainPageType.SETTINGS}
          label="设置"
          normalIcon={icMenuSettings}
          activeIcon={icMenuSettingsEd}
          isActive={mainPageMenu === MainPageType.SETTINGS}
          onClick={onModuleChange}
        />
      </div>
    </div>
  )
}
